import * as React from "react";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Checkbox } from "~/components/ui/checkbox";
import { cn } from "~/utils/cn";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  allSelectedLabel?: string;
  showAllToggle?: boolean;
  allToggleLabel?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select...",
  className,
  allSelectedLabel,
  showAllToggle = false,
  allToggleLabel = "All",
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const allSelected = selected.length === options.length;

  const triggerLabel = allSelected
    ? (allSelectedLabel || placeholder)
    : selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label || selected[0]
        : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-lg border border-border bg-card px-3 text-sm transition-colors hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-brand-green-primary focus:border-transparent",
            className
          )}
        >
          <span className={cn(
            "truncate",
            (allSelected || selected.length === 0) && "text-muted-foreground"
          )}>
            {triggerLabel}
          </span>
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1.5"
        align="start"
      >
        <div className="flex flex-col">
          {showAllToggle && (
            <>
              <button
                type="button"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors text-left font-medium",
                  allSelected
                    ? "text-black"
                    : ""
                )}
                onClick={() => {
                  onChange(allSelected ? [] : options.map((o) => o.value));
                }}
              >
                <Checkbox
                  checked={allSelected}
                  className="pointer-events-none"
                  tabIndex={-1}
                />
                <span className="leading-snug">{allToggleLabel}</span>
              </button>
              <div className="border-t border-primary/10 my-1" />
            </>
          )}
          {options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors text-left",
                  isSelected
                    ? "text-black"
                    : ""
                )}
                onClick={() => handleToggle(option.value)}
              >
                <Checkbox
                  checked={isSelected}
                  className="pointer-events-none"
                  tabIndex={-1}
                />
                <span className="leading-snug">{option.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
